import { useCallback, useMemo, useState } from 'react';
import type { EngineId } from '@geosoft/viewer-sdk';
import { Icon, Stack, tokens } from '@/design';
import { useViewerStore } from '@/state/useViewerStore';
import { buildViewerStateForUrl } from '@/state/buildViewerStateForUrl';
import { encodeView } from '@/state/url';
import { URL_PAYLOAD_HARD_LIMIT_BYTES, URL_PAYLOAD_WARN_BYTES } from '@/state/url/schema';

export interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  engine?: EngineId;
}

type ShareTier = 'read' | 'edit';
type ShareTab = 'link' | 'qr' | 'copy';
type SizeBucket = 'green' | 'yellow' | 'red';

function buildAppShareUrl(viewFragment: string) {
  if (typeof window === 'undefined') return viewFragment;
  return `${window.location.origin}${window.location.pathname}${window.location.search}${viewFragment}`;
}

function bucketFor(bytes: number): SizeBucket {
  if (bytes > URL_PAYLOAD_HARD_LIMIT_BYTES) return 'red';
  if (bytes >= URL_PAYLOAD_WARN_BYTES) return 'yellow';
  return 'green';
}

function bucketColor(bucket: SizeBucket) {
  if (bucket === 'red') return tokens.color.semantic.danger;
  if (bucket === 'yellow') return tokens.color.semantic.warning;
  return tokens.color.semantic.success;
}

/**
 * ShareModal — GVW-105 / GVW-106 / GVW-107.
 *
 * Uses native controls plus design tokens to avoid relying on unfinished
 * design primitives. QR uses an explicit SVG placeholder, per WP-E11 dispatch.
 */
export function ShareModal({ isOpen, onClose, engine = 'maplibre' }: ShareModalProps) {
  const [selectedTier, setSelectedTier] = useState<ShareTier>('read');
  const [activeTab, setActiveTab] = useState<ShareTab>('link');
  const [copySuccess, setCopySuccess] = useState(false);
  const [isCopying, setIsCopying] = useState(false);

  const camera = useViewerStore((s) => s.camera);
  const time = useViewerStore((s) => s.time);
  const visible = useViewerStore((s) => s.visibleLayerIds);
  const saved = useViewerStore((s) => s.savedViews);
  const active = useViewerStore((s) => s.activeScenarioId);
  const compare = useViewerStore((s) => s.compareWithScenarioId);

  const viewerState = useMemo(
    () =>
      buildViewerStateForUrl({
        camera,
        time,
        visibleLayerIds: visible,
        primaryEngine: engine,
        savedViews: saved,
        activeScenarioId: active,
        compareWithScenarioId: compare,
      }),
    [active, camera, compare, engine, saved, time, visible],
  );

  const share = useMemo(() => {
    try {
      const fragment = encodeView(viewerState, { readOnly: selectedTier === 'read' });
      const url = buildAppShareUrl(fragment);
      const bytes = new TextEncoder().encode(url).length;
      return {
        url,
        bytes,
        bucket: bucketFor(bytes),
        error: bytes > URL_PAYLOAD_HARD_LIMIT_BYTES ? 'Link is over the 1.8 KB limit.' : null,
      };
    } catch (error) {
      return {
        url: '',
        bytes: URL_PAYLOAD_HARD_LIMIT_BYTES + 1,
        bucket: 'red' as SizeBucket,
        error: error instanceof Error ? error.message : 'Failed to generate share link.',
      };
    }
  }, [selectedTier, viewerState]);

  const handleCopy = useCallback(async () => {
    if (!share.url || share.error) return;
    setIsCopying(true);
    try {
      await navigator.clipboard.writeText(share.url);
      setCopySuccess(true);
      window.setTimeout(() => setCopySuccess(false), 3000);
    } finally {
      setIsCopying(false);
    }
  }, [share.error, share.url]);

  if (!isOpen) return null;

  const copyDisabled = Boolean(share.error) || isCopying;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="share-modal-title"
      data-gvw-id="GVW-105"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: tokens.zIndex.modal,
        background: tokens.color.overlay.scrim,
        display: 'grid',
        placeItems: 'center',
        padding: tokens.space[4],
      }}
    >
      <section
        style={{
          width: '100%',
          maxWidth: tokens.layout.standaloneStateWide,
          background: tokens.color.surface[1],
          border: tokens.border.thin,
          borderRadius: tokens.radius.lg,
          boxShadow: tokens.elevation[3],
          color: tokens.color.ink.primary,
          fontFamily: tokens.font.family.ui,
          padding: tokens.space[5],
        }}
      >
        <Stack direction="column" gap={4}>
          <header style={{ display: 'flex', justifyContent: 'space-between', gap: tokens.space[3] }}>
            <div>
              <h2 id="share-modal-title" style={{ margin: 0, fontSize: tokens.font.size.xl }}>
                Share View
              </h2>
              <p style={{ margin: 0, color: tokens.color.ink.secondary }}>
                Create a deterministic GeoViewer link for the current state.
              </p>
            </div>
            <button type="button" onClick={onClose} aria-label="Close share modal" style={iconButtonStyle}>
              <Icon name="close" size={20} decorative />
            </button>
          </header>

          <fieldset style={{ border: 0, margin: 0, padding: 0 }}>
            <legend style={{ marginBottom: tokens.space[2], color: tokens.color.ink.secondary }}>
              Permission
            </legend>
            <Stack direction="row" gap={2}>
              {(['read', 'edit'] as const).map((tier) => (
                <button
                  key={tier}
                  type="button"
                  onClick={() => setSelectedTier(tier)}
                  aria-pressed={selectedTier === tier}
                  style={pillButtonStyle(selectedTier === tier)}
                >
                  <Icon name={tier === 'read' ? 'lock' : 'edit'} size={20} decorative />
                  {tier === 'read' ? 'Read-only' : 'Editable'}
                </button>
              ))}
            </Stack>
          </fieldset>

          <nav aria-label="Share methods" style={{ display: 'flex', gap: tokens.space[2] }}>
            {(['link', 'qr', 'copy'] as const).map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(tab)}
                aria-pressed={activeTab === tab}
                style={pillButtonStyle(activeTab === tab)}
              >
                {tab.toUpperCase()}
              </button>
            ))}
          </nav>

          {activeTab === 'link' ? (
            <section aria-label="Share link" data-gvw-id="GVW-107">
              <label style={{ display: 'block', marginBottom: tokens.space[2] }} htmlFor="share-url">
                Share URL
              </label>
              <textarea
                id="share-url"
                readOnly
                value={share.url}
                style={textareaStyle}
                rows={4}
              />
              <FooterStatus share={share} />
              <button type="button" onClick={handleCopy} disabled={copyDisabled} style={primaryButtonStyle}>
                <Icon name="content_copy" size={20} decorative />
                {isCopying ? 'Copying...' : 'Copy'}
              </button>
            </section>
          ) : null}

          {activeTab === 'qr' ? (
            <section aria-label="Share QR" data-gvw-id="GVW-106" style={{ textAlign: 'center' }}>
              <svg
                data-testid="qr-placeholder"
                role="img"
                aria-label="QR placeholder"
                viewBox="0 0 128 128"
                width={tokens.layout.popoverSm}
                height={tokens.layout.popoverSm}
                style={{
                  maxWidth: '100%',
                  background: tokens.color.surface[0],
                  border: tokens.border.thin,
                  borderRadius: tokens.radius.md,
                }}
              >
                <rect x="16" y="16" width="32" height="32" fill="currentColor" />
                <rect x="80" y="16" width="32" height="32" fill="currentColor" />
                <rect x="16" y="80" width="32" height="32" fill="currentColor" />
                <rect x="64" y="64" width="16" height="16" fill="currentColor" />
                <rect x="88" y="88" width="24" height="24" fill="currentColor" />
              </svg>
              <p style={{ color: tokens.color.ink.secondary }}>
                QR generation placeholder. Install a QR dependency in a later packaging pass if
                a real matrix code is required.
              </p>
              <FooterStatus share={share} />
            </section>
          ) : null}

          {activeTab === 'copy' ? (
            <section aria-label="Copy share link">
              <p style={{ color: tokens.color.ink.secondary }}>
                Copy the current {selectedTier === 'read' ? 'read-only' : 'editable'} link to your clipboard.
              </p>
              <button type="button" onClick={handleCopy} disabled={copyDisabled} style={primaryButtonStyle}>
                <Icon name="content_copy" size={20} decorative />
                Copy Link
              </button>
              <FooterStatus share={share} />
            </section>
          ) : null}

          <p style={{ margin: 0, fontSize: tokens.font.size.sm, color: tokens.color.ink.secondary }}>
            <Icon name="policy" size={20} decorative /> Sharing this link will create an audit row.
          </p>
          {copySuccess ? (
            <p role="status" style={{ margin: 0, color: tokens.color.semantic.success }}>
              Link copied to your clipboard.
            </p>
          ) : null}
        </Stack>
      </section>
    </div>
  );
}

function FooterStatus({
  share,
}: {
  share: { bytes: number; bucket: SizeBucket; error: string | null };
}) {
  return (
    <p style={{ color: bucketColor(share.bucket), fontSize: tokens.font.size.sm }}>
      {share.bytes} bytes
      {share.bucket === 'yellow' ? ' — approaching the 1.8 KB limit' : ''}
      {share.error ? ` — ${share.error}` : ''}
    </p>
  );
}

const iconButtonStyle = {
  minHeight: tokens.density.touchMin,
  minWidth: tokens.density.touchMin,
  border: tokens.border.thin,
  borderRadius: tokens.radius.input,
  background: tokens.color.surface[2],
  color: tokens.color.ink.primary,
  cursor: 'pointer',
};

function pillButtonStyle(active: boolean) {
  return {
    minHeight: tokens.density.touchMin,
    border: tokens.border.thin,
    borderRadius: tokens.radius.pill,
    background: active ? tokens.color.accent.primary : tokens.color.surface[2],
    color: active ? tokens.color.accent.on : tokens.color.ink.primary,
    paddingInline: tokens.space[3],
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.space[2],
    cursor: 'pointer',
  };
}

const textareaStyle = {
  width: '100%',
  boxSizing: 'border-box' as const,
  border: tokens.border.thin,
  borderRadius: tokens.radius.input,
  background: tokens.color.surface[0],
  color: tokens.color.ink.primary,
  fontFamily: tokens.font.family.mono,
  fontSize: tokens.font.size.sm,
  padding: tokens.space[3],
};

const primaryButtonStyle = {
  minHeight: tokens.density.touchMin,
  border: tokens.border.thin,
  borderRadius: tokens.radius.input,
  background: tokens.color.accent.primary,
  color: tokens.color.accent.on,
  paddingInline: tokens.space[4],
  display: 'inline-flex',
  alignItems: 'center',
  gap: tokens.space[2],
  cursor: 'pointer',
};
